import { Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { ModuleDataDB, StateProperty } from '@app/common';
import { getVisibleSections } from '../../../services/DecisionTreeService/DecisionTreeService';
import Section from '../Section/Section';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { setTransientStateProperties } from '../../../store/moduleStateSlice';
import PickSessionPropertyModal from './PickSessionPropertyModal';

type Properties = {
    module: ModuleDataDB;
    sessionFinished: (session?: `slot_${string}__session_${string}`) => void;
};

const Session = (props: Properties) => {
    const { slotId, sessionId, sectionId } = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const moduleState = useAppSelector(state => state.moduleState.currentModuleState);
    const transientStateProperties = useAppSelector(state => state.moduleState.transientStateProperties);
    const config = useAppSelector(state => state.globalState.config);
    const user = useAppSelector(state => state.globalState.user);
    const userProperties = user?.userProperties || [];

    if (!slotId || !sessionId || !sectionId) {
        throw 'Session not found';
    }

    const session = props.module.slots[slotId as `slot_${string}`].sessions[sessionId as `session_${string}`];
    const visibleSections = getVisibleSections(
        session,
        moduleState.modifiers,
        moduleState.elementValues,
        userProperties,
        transientStateProperties
    );

    const visibleSectionIds = visibleSections.map(section => section.id);
    if (visibleSectionIds.length !== new Set(visibleSectionIds).size) {
        throw new Error('Visible sections for this session contain duplicates. This would lead to infinite navigation.');
    }

    const currentIndex = visibleSections.findIndex(section => section.id === sectionId);

    const goToNextSection = () => {
        if (currentIndex < 0) {
            console.error(
                `Cannot navigate from ${sectionId}: the current section is not visible in ${slotId}/${sessionId}.`
            );
            return;
        }
        if (currentIndex === visibleSections.length - 1) {
            props.sessionFinished(`${slotId}__${sessionId}` as `slot_${string}__session_${string}`);
        } else {
            navigate(`../slots/${slotId}/${sessionId}/${visibleSections[currentIndex + 1].id}`);
        }
    };

    const getGoToPreviousSection = () => {
        if (currentIndex <= 0) {
            return undefined;
        } else {
            return () => navigate(`../slots/${slotId}/${sessionId}/${visibleSections[currentIndex - 1].id}/last`);
        }
    };

    const showDoneButton = currentIndex === visibleSections.length - 1;
    if (transientStateProperties.length === 0 && config?.enableTransientSessionModes === true) {
        const currentIndexInAllSections = session.sections.findIndex(section => section.id === sectionId);
        const currentSectionRecommendation = session.sections[currentIndexInAllSections].recommendationRule;
        
        const setRecommendationProperties = (properties: StateProperty[]) => {
            dispatch(setTransientStateProperties(properties));
        };

        return <PickSessionPropertyModal 
            currentSectionRecommdation={currentSectionRecommendation}
            setTransientStateProperties={setRecommendationProperties}
        />;
    } else {
        return (<>
            <Routes>
                <Route path="">
                    <Route
                        path=""
                        element={<Section
                            sectionId={sectionId as any}
                            navigateNext={goToNextSection}
                            navigatePrevious={getGoToPreviousSection()}
                            showDoneButtonAfterSection={showDoneButton}
                        />}
                    />
                    <Route
                        path=":itemId/*"
                        element={<Section
                            sectionId={sectionId as any}
                            navigateNext={goToNextSection}
                            navigatePrevious={getGoToPreviousSection()}
                            showDoneButtonAfterSection={showDoneButton}
                        />}
                    />
                </Route>
            </Routes>
        </>);
    }
};
export default Session;